"use client";

import React, { useState } from "react";
import { Atom, Cpu, Wrench, Calculator, Compass, Hammer, Terminal, RefreshCw, Award, Layers, CircleCheck as CheckCircle2, Info, CalendarCheck, Bot, ArrowRight, Workflow, Sparkles, ShieldCheck, RotateCcw, ChevronRight } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
interface TranslationContent {
  headerBadge: string;
  title: string;
  subtitle: string;
  stemTitle: string;
  stemSubtitle: string;
  methodologyTitle: string;
  methodologySubtitle: string;
  assessmentTitle: string;
  assessmentSubtitle: string;
  assessmentNoticeTitle: string;
  assessmentNoticeText: string;
  criteriaLabel: string;
  stemItems: {
    letter: string;
    title: string;
    domain: string;
    desc: string;
    topics: string[];
  }[];
  steps: {
    num: string;
    title: string;
    desc: string;
    focus: string;
  }[];
  milestones: {
    level: string;
    project: string;
    format: string;
    badge: string;
  }[];
  criteria: string[];
}
const contentData: Record<"vi" | "en", TranslationContent> = {
  vi: {
    headerBadge: "PHƯƠNG PHÁP & ĐÁNH GIÁ NĂNG LỰC",
    title: "Khung năng lực STEM, tiến trình buổi học & đánh giá thực tế",
    subtitle: "Chương trình ROBOTO gắn kết chặt chẽ khoa học ứng dụng, cấu trúc buổi học 5 bước chuẩn quốc tế và hệ thống dự án đánh giá năng lực thực hành không áp đặt thi cử nặng nề.",
    stemTitle: "Khung năng lực liên môn STEM tích hợp",
    stemSubtitle: "Mỗi bài học không chỉ dừng lại ở việc lắp ráp mô hình mà tích hợp có hệ thống 4 thành tố kiến thức theo chiều sâu.",
    methodologyTitle: "Cách mỗi buổi học diễn ra (Cấu trúc 5 bước)",
    methodologySubtitle: "Quy trình sư phạm chuẩn hoá giúp học sinh từ chỗ tiếp nhận vấn đề đến sáng tạo giải pháp và tự tin trình bày.",
    assessmentTitle: "Đánh giá năng lực & dự án hoàn thành khóa học",
    assessmentSubtitle: "Tiến độ của học sinh được đo lường qua sản phẩm thực tế, trạm kiểm tra kỹ năng và các ngày hội mô phỏng thi đấu.",
    assessmentNoticeTitle: "Nguyên tắc đánh giá thực chất tại ROBOTO",
    assessmentNoticeText: "Chương trình áp dụng tiêu chí thực hành, đánh giá sản phẩm, trạm kỹ năng, dự án thực tế và mô phỏng thi đấu thử nghiệm; trung tâm không cấp chứng chỉ học thuật hình thức hay văn bằng hàn lâm thay thế trường học phổ thông.",
    criteriaLabel: "Các hình thức đo lường chính:",
    stemItems: [{
      letter: "S",
      title: "Science",
      domain: "Khoa học thực nghiệm",
      desc: "Khám phá các quy luật vật lý và thế giới quan khoa học tự nhiên thông qua hoạt động tương tác vật lý thực.",
      topics: ["Chuyển động (movement)", "Lực & phản lực (force)", "Dòng điện & mạch (electricity)", "Cảm biến tương tác (sensors)", "Phản xạ ánh sáng (light reflection)", "Cảm biến mức xám (grayscale)", "Ma sát (friction)", "Cân bằng động & tĩnh (balance)"]
    }, {
      letter: "T",
      title: "Technology",
      domain: "Công nghệ & Lập trình",
      desc: "Làm chủ môi trường lập trình khối, cảm biến thông minh và tư duy cấu trúc lệnh từ cơ bản đến phức tạp.",
      topics: ["Block Studio IDE", "Tư duy logic (programming logic)", "Xử lý tín hiệu cảm biến (sensors)", "Biến số & bộ đếm (variables)", "Vòng lặp có điều kiện (loops)", "Hàm tùy biến (My Blocks)"]
    }, {
      letter: "E",
      title: "Engineering",
      domain: "Tư duy kỹ thuật cơ khí",
      desc: "Thiết kế, chế tạo kết cấu bền vững, hệ thống truyền động và tối ưu hóa giải pháp giải quyết nhiệm vụ.",
      topics: ["Cơ cấu cơ học (mechanisms)", "Bánh răng truyền động (gears)", "Hệ thống đòn bẩy (levers)", "Khung gầm chịu lực (chassis)", "Cơ cấu thực thi nhiệm vụ (mission mechanisms)", "Thử nghiệm và cải tiến lặp (testing & improvement)"]
    }, {
      letter: "M",
      title: "Mathematics",
      domain: "Toán học ứng dụng",
      desc: "Ứng dụng các khái niệm số học, hình học và thuật toán thực tiễn để định lượng chính xác hành vi robot.",
      topics: ["Đếm & số học (counting)", "Đo lường thực địa (measurement)", "Chu vi bánh xe & quãng đường (wheel circumference)", "Khoảng cách (distance)", "Góc xoay & độ lệch (angles)", "Ngưỡng cảm biến (thresholds)", "Bản đồ lộ trình (route maps)", "Giá trị trung bình & sai số (averages, error)"]
    }],
    steps: [{
      num: "01",
      title: "Khởi động",
      desc: "Câu chuyện / tình huống thực tế / khám phá kiến thức mới",
      focus: "Kích hoạt sự tò mò qua câu hỏi gợi mở, đặt ra bài toán công nghệ cần giải quyết trong ngày."
    }, {
      num: "02",
      title: "Lắp ráp",
      desc: "Làm việc nhóm / lắp mô hình kỹ thuật",
      focus: "Rèn luyện kỹ năng đọc bản vẽ không gian 3D, phối hợp đồng đội 2–3 học sinh trên mỗi bộ công cụ."
    }, {
      num: "03",
      title: "Lập trình / Tư duy",
      desc: "Lập trình khối hoặc trò chơi tư duy lập trình (Duplo)",
      focus: "Xây dựng thuật toán, giải mã lệnh logic; với nhóm mầm non Duplo là chuỗi thẻ lệnh không màn hình."
    }, {
      num: "04",
      title: "Thử nghiệm – Sửa lỗi – Cải tiến",
      desc: "Kiểm tra thực địa / phân tích lỗi / tối ưu thông số",
      focus: "Robot được đưa vào sa bàn thực nghiệm; học sinh tự đo đạc dữ liệu sai lệch và tinh chỉnh cơ khí lẫn mã lệnh."
    }, {
      num: "05",
      title: "Thuyết trình",
      desc: "Trình diễn sản phẩm / chia sẻ ý tưởng / nhận xét",
      focus: "Rèn sự tự tin, kỹ năng phản biện kỹ thuật, giải thích nguyên lý hoạt động trước thầy cô và bạn học."
    }],
    milestones: [{
      level: "Khóa D1 (Duplo)",
      project: "Ngày hội Kỹ sư nhí",
      format: "Trạm thực hành kết cấu vững & chuyển động cơ bản",
      badge: "Duplo 4–5 tuổi"
    }, {
      level: "Khóa D2 (Duplo)",
      project: "Thành phố tí hon",
      format: "Mô hình đô thị tích hợp bánh răng & cơ cấu nâng-hạ",
      badge: "Duplo 4–5 tuổi"
    }, {
      level: "Khóa D3 (Duplo)",
      project: "Thuyết trình mô hình & flowchart",
      format: "Diễn giải lưu đồ tư duy hình học và mô hình tay gắp",
      badge: "Duplo 5–6 tuổi"
    }, {
      level: "Khóa D4 (Duplo)",
      project: "Ngày hội robot (Tốt nghiệp cầu nối)",
      format: "Đánh giá thực hành tích hợp động cơ, pin & công tắc chuyển tiếp WhalesBot",
      badge: "Cầu nối D4"
    }, {
      level: "Khóa HP1 (WhalesBot)",
      project: "Dự án Công viên cơ khí",
      format: "Mô hình lập trình chuỗi lệnh kiểm soát góc quay & tốc độ",
      badge: "WhalesBot 7+"
    }, {
      level: "Khóa HP2 (WhalesBot)",
      project: "Nhà máy tí hon",
      format: "Dây chuyền tích hợp cảm biến hồng ngoại & chạm phản hồi môi trường",
      badge: "WhalesBot 7+"
    }, {
      level: "Khóa HP3 (WhalesBot)",
      project: "Giao hàng thành phố lưới",
      format: "Xe robot điều hướng chuẩn xác góc quay 90°/180° theo chu vi bánh",
      badge: "WhalesBot 7+"
    }, {
      level: "Khóa HP4 (WhalesBot)",
      project: "Robot dọn sân trường",
      format: "Xe robot thông minh tự động né chướng ngại vật & chống rơi bậc thang",
      badge: "WhalesBot 7+"
    }, {
      level: "Khóa HP6 (WhalesBot)",
      project: "Mô phỏng Mini Invention Trials",
      format: "Thử thách giải quyết nhiệm vụ sa bàn kín 150 giây & nhiệm vụ bí mật",
      badge: "Chuẩn bị thi đấu"
    }],
    criteria: ["Tiêu chí thực hành chuẩn hóa (Practical Criteria)", "Đánh giá thành phẩm vận hành (Product Evaluation)", "Trạm kiểm tra kỹ năng độc lập (Skills Stations)", "Dự án sáng tạo cá nhân và nhóm (Project Work)", "Thuyết trình và phản biện kỹ thuật (Demonstrations & Presentations)", "Mô phỏng thi đấu sa bàn áp lực thời gian (Simulated Competitions)"]
  },
  en: {
    headerBadge: "METHODOLOGY & COMPETENCY ASSESSMENT",
    title: "STEM Framework, Lesson Dynamics & Practical Assessment",
    subtitle: "ROBOTO connects applied science, an internationally verified 5-stage lesson delivery model, and hands-on milestone project assessments without superficial test drills.",
    stemTitle: "Integrated STEM Competency Framework",
    stemSubtitle: "Every single class builds beyond toy construction, methodically merging four distinct academic disciplines.",
    methodologyTitle: "How Each Lesson Unfolds (5-Step Structure)",
    methodologySubtitle: "A standardised pedagogical progression guiding young minds from problem discovery to prototyping and peer demonstration.",
    assessmentTitle: "Assessment & Capstone Projects",
    assessmentSubtitle: "Students' technical progress is evaluated through functional hardware, skill stations, and tournament-style challenges.",
    assessmentNoticeTitle: "Authentic Assessment Guidelines at ROBOTO",
    assessmentNoticeText: "The curriculum relies on practical criteria, operational product evaluation, skills stations, project demonstrations, and simulated arena challenges; ROBOTO does not issue formal academic diplomas or university certifications.",
    criteriaLabel: "Core Assessment Modalities:",
    stemItems: [{
      letter: "S",
      title: "Science",
      domain: "Experimental Sciences",
      desc: "Discover real physical laws and scientific phenomena through direct interaction with concrete physical mechanisms.",
      topics: ["Movement & kinetics", "Forces & counteraction", "Electrical circuits", "Sensor principles", "Light reflection", "Grayscale sensing", "Frictional coefficients", "Dynamic & static balance"]
    }, {
      letter: "T",
      title: "Technology",
      domain: "Technology & Coding",
      desc: "Master the Block Studio IDE, hardware sensors, and algorithmic structures from sequence to modular functions.",
      topics: ["Block Studio platform", "Programming logic", "Sensor processing", "Variables & state counters", "Condition loops", "Modular My Blocks"]
    }, {
      letter: "E",
      title: "Engineering",
      domain: "Mechanical Design",
      desc: "Design and build durable structural frames, power transmission systems, and mission-ready robotic attachments.",
      topics: ["Mechanical linkages", "Gear trains", "Lever mechanisms", "Rigid chassis design", "Mission attachments", "Testing and iteration"]
    }, {
      letter: "M",
      title: "Mathematics",
      domain: "Applied Mathematics",
      desc: "Utilise arithmetic, spatial geometry, and quantitative formulas to calculate accurate autonomous robot maneuvers.",
      topics: ["Counting & sequences", "Spatial measurement", "Wheel circumference math", "Distance calculations", "Angles & degree offsets", "Sensor thresholds", "Route coordinate maps", "Averages & error tolerances"]
    }],
    steps: [{
      num: "01",
      title: "Warm-Up",
      desc: "Story / real situation / exploration of new concept",
      focus: "Ignites curiosity through contextual engineering scenarios and clear challenge goals."
    }, {
      num: "02",
      title: "Assembly",
      desc: "Teamwork / mechanical model building",
      focus: "Develops 3D spatial diagram reading and cooperative collaboration in groups of 2–3 students."
    }, {
      num: "03",
      title: "Coding / Logic",
      desc: "Block coding or unplugged algorithmic cards (Duplo)",
      focus: "Constructs algorithmic logic flow; young Duplo learners navigate screenless directional sequences."
    }, {
      num: "04",
      title: "Testing – Debugging – Optimization",
      desc: "Field testing / error debugging / mechanical improvement",
      focus: "Robots run live on custom test mats; learners log deviations and refine mechanisms and code parameters."
    }, {
      num: "05",
      title: "Presentation",
      desc: "Product demonstration / idea sharing / peer review",
      focus: "Fosters confidence and technical communication by explaining underlying engineering concepts."
    }],
    milestones: [{
      level: "Course D1 (Duplo)",
      project: "Junior Engineer Festival",
      format: "Hands-on stations for structural stability and basic movements",
      badge: "Duplo Ages 4–5"
    }, {
      level: "Course D2 (Duplo)",
      project: "Tiny City",
      format: "Urban models combining gear trains and lift-lower levers",
      badge: "Duplo Ages 4–5"
    }, {
      level: "Course D3 (Duplo)",
      project: "Model & Flowchart Presentation",
      format: "Explaining physical flowchart cards and gripper arm prototypes",
      badge: "Duplo Ages 5–6"
    }, {
      level: "Course D4 (Duplo)",
      project: "Robot Festival (Bridge Capstone)",
      format: "Practical evaluation combining electric motors, switches, and transition logic",
      badge: "D4 Bridge"
    }, {
      level: "Course HP1 (WhalesBot)",
      project: "Mechanical Theme Park",
      format: "Sequential code governing angular speed and motor direction",
      badge: "WhalesBot 7+"
    }, {
      level: "Course HP2 (WhalesBot)",
      project: "Tiny Automated Factory",
      format: "Interactive assembly line triggered by infrared and tactile sensors",
      badge: "WhalesBot 7+"
    }, {
      level: "Course HP3 (WhalesBot)",
      project: "Grid City Delivery",
      format: "Standard chassis executing 90°/180° rotations based on wheel circumference",
      badge: "WhalesBot 7+"
    }, {
      level: "Course HP4 (WhalesBot)",
      project: "Campus Clean-up Robot",
      format: "Smart mobile platform with obstacle avoidance and anti-drop safety",
      badge: "WhalesBot 7+"
    }, {
      level: "Course HP6 (WhalesBot)",
      project: "Mini Invention Trials Simulation",
      format: "150-second mission challenge on mat with secret surprise objectives",
      badge: "Tournament Prep"
    }],
    criteria: ["Standardised Practical Criteria", "Operational Product Evaluation", "Independent Skills Stations", "Project-based Collaborative Work", "Demonstrations & Technical Presentations", "Simulated Arena Timed Competitions"]
  }
};
export default function LearningMethodologyAssessment() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  const c = contentData[lang];
  return <section id="learningmethodologyassessment" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden isolate">
      {/* Decorative ambient subtle background glow */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute top-12 left-1/2 -translate-x-1/2 w-[48rem] h-[24rem] bg-muted/40 rounded-full blur-3xl opacity-50" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl opacity-40" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header with Language Switcher */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div className="max-w-3xl space-y-3">
            <div className="inline-flex items-center gap-2">
              <Badge variant="outline" className="text-primary border-primary/20 bg-primary/5 px-3 py-1 font-semibold uppercase tracking-wider text-xs">
                {c.headerBadge}
              </Badge>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight font-default">
              {c.title}
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
              {c.subtitle}
            </p>
          </div>

          {/* Language Toggle */}
          <div className="flex items-center gap-2 self-start md:self-auto bg-card border border-border p-1 rounded-lg shadow-2xs">
            <Button type="button" size="sm" variant={lang === "vi" ? "default" : "ghost"} onClick={() => setLang("vi")} className="text-xs px-3 font-semibold h-8" aria-label="Chuyển sang tiếng Việt">
              Tiếng Việt
            </Button>
            <Button type="button" size="sm" variant={lang === "en" ? "default" : "ghost"} onClick={() => setLang("en")} className="text-xs px-3 font-semibold h-8" aria-label="Switch to English">
              English
            </Button>
          </div>
        </div>

        {/* =========================================================================
            PART A: STEM FRAMEWORK (SCIENCE, TECHNOLOGY, ENGINEERING, MATHEMATICS)
            ========================================================================= */}
        <div className="mb-24">
          <div className="mb-8">
            <div className="flex items-center gap-2 text-primary font-semibold text-sm uppercase tracking-wide">
              <Layers className="size-4" />
              <span>Part A</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold mt-1 tracking-tight font-default">
              {c.stemTitle}
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base mt-2 max-w-2xl">
              {c.stemSubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {c.stemItems.map((item, idx) => {
            const iconMap = [<Atom key="sci" className="size-6 text-primary" />, <Cpu key="tech" className="size-6 text-accent" />, <Wrench key="eng" className="size-6 text-secondary" />, <Calculator key="math" className="size-6 text-chart-4" />];
            return <Card key={idx} data-index={idx} className="bg-card text-card-foreground border-border shadow-sm hover:shadow-md transition-shadow relative overflow-hidden flex flex-col">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="size-11 rounded-lg bg-muted flex items-center justify-center border border-border">
                        {iconMap[idx]}
                      </div>
                      <span className="text-4xl font-extrabold text-muted-foreground/30 font-default">
                        {item.letter}
                      </span>
                    </div>
                    <CardTitle className="text-xl font-bold text-foreground">
                      {item.title}
                    </CardTitle>
                    <p className="text-xs font-medium text-primary">
                      {item.domain}
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-4 flex-1 flex flex-col justify-between">
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {item.desc}
                    </p>

                    <div className="space-y-1.5 pt-2 border-t border-border/60">
                      <p className="text-[11px] font-semibold tracking-wider text-foreground uppercase">
                        {lang === "vi" ? "Nội dung tích hợp:" : "Included Concepts:"}
                      </p>
                      <ul className="space-y-1">
                        {item.topics.map((topic, tIdx) => <li key={tIdx} data-index={tIdx} className="text-xs text-muted-foreground flex items-center gap-1.5">
                            <span className="size-1.5 rounded-full bg-primary/60 shrink-0" />
                            <span>{topic}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        <Separator className="my-16" />

        {/* =========================================================================
            PART B: CÁCH MỖI BUỔI HỌC DIỄN RA (5-STEP STRUCTURE)
            ========================================================================= */}
        <div className="mb-24">
          <div className="mb-10">
            <div className="flex items-center gap-2 text-primary font-semibold text-sm uppercase tracking-wide">
              <CalendarCheck className="size-4" />
              <span>Part B</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold mt-1 tracking-tight font-default">
              {c.methodologyTitle}
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base mt-2 max-w-2xl">
              {c.methodologySubtitle}
            </p>
          </div>

          {/* =========================================================================
              VISUAL FLOW DIAGRAM: TECHNICAL METHODOLOGY & ASSESSMENT PIPELINE
              Deep-blue/white technical branding visual architecture
              ========================================================================= */}
          <div className="mb-12 rounded-2xl bg-[#0A1128] text-white border border-blue-900/60 p-6 sm:p-8 lg:p-10 shadow-xl relative overflow-hidden isolate">
            {/* Precision Blueprint Grid Overlay */}
            <div aria-hidden="true" className="absolute inset-0 -z-10 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:28px_28px] opacity-80" />
            {/* Tech Corner Decorative Accents */}
            <div aria-hidden="true" className="absolute top-0 left-0 w-24 h-24 border-t-2 border-l-2 border-accent/40 rounded-tl-2xl pointer-events-none" />
            <div aria-hidden="true" className="absolute bottom-0 right-0 w-24 h-24 border-b-2 border-r-2 border-secondary/40 rounded-br-2xl pointer-events-none" />

            {/* Diagram Header Bar */}
            <div className="flex flex-wrap items-center justify-between gap-4 pb-6 mb-8 border-b border-blue-900/80">
              <div className="flex items-center gap-3">
                <div className="size-9 rounded-lg bg-primary/20 border border-primary/40 flex items-center justify-center text-primary-foreground">
                  <Workflow className="size-5 text-accent" />
                </div>
                <div>
                  <div className="text-[10px] font-bold tracking-widest text-secondary uppercase font-mono">
                    ROBOTO TECHNICAL PIPELINE
                  </div>
                  <h4 className="text-base sm:text-lg font-bold text-white font-default">
                    {lang === "vi" ? "Sơ đồ luồng phương pháp sư phạm & chuỗi đánh giá" : "Methodology & Assessment Flow Architecture"}
                  </h4>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-blue-950/80 text-blue-200 border-blue-700/50 text-[11px] font-mono px-3 py-1">
                  <span className="size-1.5 rounded-full bg-emerald-400 animate-pulse mr-1.5" />
                  {lang === "vi" ? "QUY TRÌNH 5 BƯỚC THỰC THI" : "5-STAGE EXECUTION ENGINE"}
                </Badge>
              </div>
            </div>

            {/* Desktop & Tablet Connected Horizontal Flow Nodes */}
            <div className="relative">
              {/* Connecting Rail Line behind nodes (Desktop) */}
              <div aria-hidden="true" className="hidden lg:block absolute top-[52px] left-8 right-8 h-1 bg-gradient-to-r from-primary via-accent to-secondary rounded-full -z-0 opacity-70" />

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 relative z-10">
                {c.steps.map((st, sIdx) => {
                const nodeIcons = [<Compass key="step1" className="size-5 text-sky-400" />, <Hammer key="step2" className="size-5 text-amber-400" />, <Terminal key="step3" className="size-5 text-emerald-400" />, <RefreshCw key="step4" className="size-5 text-cyan-400" />, <Award key="step5" className="size-5 text-rose-400" />];
                return <div key={sIdx} data-index={sIdx} className="group flex flex-col bg-[#131F42]/90 hover:bg-[#182854] border border-blue-800/60 hover:border-accent/80 rounded-xl p-5 transition-all duration-300 shadow-md relative">
                      {/* Step Badge Node Indicator */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="size-11 rounded-lg bg-[#0A1128] border border-blue-700/60 flex items-center justify-center shadow-inner group-hover:scale-105 transition-transform">
                          {nodeIcons[sIdx]}
                        </div>
                        <span className="text-xs font-mono font-bold text-blue-300/80 bg-blue-950 px-2.5 py-1 rounded-md border border-blue-800/80">
                          {st.num}
                        </span>
                      </div>

                      {/* Title & Tag */}
                      <h5 className="text-base font-bold text-white mb-1 font-default group-hover:text-accent transition-colors">
                        {st.title}
                      </h5>
                      <p className="text-[11px] font-semibold text-secondary mb-3 leading-snug">
                        {st.desc}
                      </p>

                      {/* Core Technical Focus */}
                      <p className="text-xs text-blue-100/70 leading-relaxed mt-auto pt-2 border-t border-blue-900/60">
                        {st.focus}
                      </p>

                      {/* Step Connector Arrow (Desktop layout, between nodes) */}
                      {sIdx < c.steps.length - 1 && <div aria-hidden="true" className="hidden lg:flex absolute -right-4 top-10 size-7 bg-[#0A1128] border border-blue-700/80 rounded-full items-center justify-center z-20 text-blue-300 shadow-sm">
                          <ChevronRight className="size-4" />
                        </div>}

                      {/* Loop Indicator Callout for Step 04 Iteration */}
                      {sIdx === 3 && <div className="mt-3 pt-2 border-t border-cyan-500/30 flex items-center gap-1.5 text-[10px] text-cyan-300 font-mono">
                          <RotateCcw className="size-3 animate-spin shrink-0" style={{
                      animationDuration: '8s'
                    }} />
                          <span>{lang === "vi" ? "Vòng lặp cải tiến lặp lại" : "Iterative refinement loop"}</span>
                        </div>}
                    </div>;
              })}
              </div>
            </div>

            {/* Assessment Pipeline Convergence Banner */}
            <div className="mt-8 pt-6 border-t border-blue-900/80 flex flex-col lg:flex-row items-center justify-between gap-5 bg-blue-950/60 rounded-xl p-5 border-l-4 border-l-accent">
              <div className="flex items-center gap-4">
                <div className="size-10 rounded-lg bg-accent/20 border border-accent/40 flex items-center justify-center text-accent shrink-0">
                  <ShieldCheck className="size-6 text-accent" />
                </div>
                <div>
                  <div className="text-xs font-bold font-mono text-accent uppercase tracking-wider">
                    {lang === "vi" ? "KẾT QUẢ ĐÁNH GIÁ CHUẨN NĂNG LỰC" : "VERIFIED COMPETENCY OUTPUT"}
                  </div>
                  <p className="text-xs sm:text-sm text-blue-100/90 font-medium">
                    {lang === "vi" ? "Tiến trình 5 bước chuyển hóa trực tiếp thành hồ sơ năng lực qua 6 trạm kiểm tra & dự án capstone thực tế." : "The 5-stage progression directly transforms into verified competency profiles via skill stations & capstones."}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs font-semibold text-secondary shrink-0 bg-[#0A1128] px-4 py-2 rounded-lg border border-secondary/30">
                <Sparkles className="size-4" />
                <span>{lang === "vi" ? "Chuyển sang Part C: Chi tiết đánh giá" : "Proceed to Part C: Assessment Details"}</span>
                <ArrowRight className="size-4" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {c.steps.map((st, sIdx) => {
            const stepIcons = [<Compass key="step1" className="size-5 text-primary" />, <Hammer key="step2" className="size-5 text-accent" />, <Terminal key="step3" className="size-5 text-secondary" />, <RefreshCw key="step4" className="size-5 text-chart-4" />, <Award key="step5" className="size-5 text-chart-1" />];
            return <Card key={sIdx} data-index={sIdx} className="bg-card text-card-foreground border-border shadow-xs hover:border-primary/40 transition-colors flex flex-col justify-between">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between mb-2">
                      <div className="size-8 rounded-md bg-muted flex items-center justify-center">
                        {stepIcons[sIdx]}
                      </div>
                      <span className="text-sm font-bold text-muted-foreground/60 font-mono">
                        {st.num}
                      </span>
                    </div>
                    <CardTitle className="text-lg font-bold">
                      {st.title}
                    </CardTitle>
                    <p className="text-xs font-medium text-primary leading-tight mt-1">
                      {st.desc}
                    </p>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {st.focus}
                    </p>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        <Separator className="my-16" />

        {/* =========================================================================
            PART C: ASSESSMENT & FINAL PROJECTS (REAL EXAMPLES & OFFICIAL NOTICE)
            ========================================================================= */}
        <div>
          <div className="mb-8">
            <div className="flex items-center gap-2 text-primary font-semibold text-sm uppercase tracking-wide">
              <Bot className="size-4" />
              <span>Part C</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold mt-1 tracking-tight font-default">
              {c.assessmentTitle}
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base mt-2 max-w-2xl">
              {c.assessmentSubtitle}
            </p>
          </div>

          {/* Policy / Realistic Standards Notice */}
          <Alert className="mb-10 bg-card border-border shadow-2xs">
            <Info className="size-5 text-primary" />
            <div className="space-y-1">
              <AlertTitle className="text-base font-semibold text-foreground">
                {c.assessmentNoticeTitle}
              </AlertTitle>
              <AlertDescription className="text-sm text-muted-foreground leading-relaxed">
                {c.assessmentNoticeText}
              </AlertDescription>
            </div>
          </Alert>

          {/* Criteria Pills Row */}
          <div className="mb-8 p-4 rounded-xl bg-muted/60 border border-border">
            <p className="text-xs font-semibold uppercase tracking-wider text-foreground mb-3">
              {c.criteriaLabel}
            </p>
            <div className="flex flex-wrap gap-2">
              {c.criteria.map((crit, crIdx) => <div key={crIdx} data-index={crIdx} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-card border border-border text-xs text-foreground font-medium shadow-2xs">
                  <CheckCircle2 className="size-3.5 text-secondary" />
                  <span>{crit}</span>
                </div>)}
            </div>
          </div>

          {/* Concrete Capstone Milestones Showcase */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {c.milestones.map((ms, mIdx) => <div key={mIdx} data-index={mIdx} className="bg-card text-card-foreground border border-border rounded-xl p-5 shadow-xs hover:border-primary/40 transition-colors flex flex-col justify-between gap-3">
                <div className="flex items-start justify-between gap-2">
                  <Badge variant="secondary" className="text-[11px] font-semibold px-2 py-0.5">
                    {ms.badge}
                  </Badge>
                  <span className="text-xs font-bold text-muted-foreground/80 font-mono">
                    {ms.level}
                  </span>
                </div>

                <div>
                  <h4 className="text-base font-bold text-foreground">
                    {ms.project}
                  </h4>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    {ms.format}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}